import { Tooltip as HeroTooltip } from '@heroui/react';
import { TooltipTarget } from '@/components/ui/TooltipTarget';

const INTERACTIVE = new Set(['button', 'a', 'input', 'select', 'textarea']);

type TargetChild = React.ComponentProps<typeof TooltipTarget>['children'];
/** A plain interactive DOM element (button, link, field) — TooltipTarget's input. */
function isInteractiveElement(el: React.ReactElement): el is TargetChild {
  return typeof el.type === 'string' && INTERACTIVE.has(el.type);
}

/**
 * Standardised hover-tooltip wrapper. Replaces native HTML `title="..."`
 * attributes which show a slow, ugly, OS-styled bubble after ~1.5 s.
 * Backed by HeroUI's Tooltip (React Aria) — instant, themed, with arrow.
 *
 * HeroUI tooltips don't need a provider, but we keep {@link TooltipProvider}
 * as a passthrough so the existing app-root call site stays untouched.
 */

export function TooltipProvider(props: { children: React.ReactNode }) {
  return <>{props.children}</>;
}

export function Tooltip(props: {
  label: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * The trigger — a HeroUI interactive component (Button, Popover.Trigger, …)
   * or a plain element (<button>, <a>, <div>). See the wrapping note below.
   */
  children: React.ReactElement;
}) {
  // HeroUI wires hover handling itself ONLY for its own interactive
  // components. A plain DOM child received no handlers at all, so ~29 call
  // sites (every hand-rolled icon <button>, the volume slider row, the new
  // download link) showed nothing on hover — the bug hid because each of them
  // also carries an aria-label, so only the visual hint was missing.
  //
  // Detect by element type: React sets `type` to a string for intrinsic
  // elements and to a function/object for components. Plain elements get
  // HeroUI's documented Trigger wrapper; components are passed through
  // untouched, so no HeroUI Button gains a second nested role="button".
  //
  // A plain button/link/field becomes the trigger itself (TooltipTarget):
  // Trigger's own div role="button" around it was a button inside a button —
  // two Tab stops and the label read twice (U7). Only non-interactive plain
  // elements (a <div> row, a <span>) still need Trigger to be focusable.
  const child = props.children;
  const intrinsic = typeof child.type === 'string';

  return (
    <HeroTooltip delay={250} closeDelay={0}>
      {isInteractiveElement(child) ? (
        <TooltipTarget>{child}</TooltipTarget>
      ) : intrinsic ? (
        <HeroTooltip.Trigger>{child}</HeroTooltip.Trigger>
      ) : (
        child
      )}
      <HeroTooltip.Content showArrow placement={props.side ?? 'top'} className="text-[11px]">
        <HeroTooltip.Arrow />
        {props.label}
      </HeroTooltip.Content>
    </HeroTooltip>
  );
}
